import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface UserSystemInfo {
  id: string;
  name: string;
  code: string;
  app_base_path: string;
  is_enabled: boolean;
}

export interface AvailableSystemInfo {
  id: string;
  name: string;
  code: string;
  app_base_path: string;
  is_available_in_plan: boolean;
  user_has_access: boolean;
}

export function useUserSystemAssignments(userId?: string) {
  return useQuery({
    queryKey: ['user-system-assignments', userId],
    queryFn: async (): Promise<UserSystemInfo[]> => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('user_systems')
        .select(`
          is_enabled,
          systems:system_id (
            id,
            name,
            code,
            app_base_path
          )
        `)
        .eq('user_id', userId);

      if (error) throw error;
      
      return data?.map(item => ({
        id: Array.isArray(item.systems) ? item.systems[0]?.id || '' : item.systems?.id || '',
        name: Array.isArray(item.systems) ? item.systems[0]?.name || '' : item.systems?.name || '',
        code: Array.isArray(item.systems) ? item.systems[0]?.code || '' : item.systems?.code || '',
        app_base_path: Array.isArray(item.systems) ? item.systems[0]?.app_base_path || '' : item.systems?.app_base_path || '',
        is_enabled: item.is_enabled
      })) || [];
    },
    enabled: !!userId,
  });
}

export function useAvailableSystemsForUser(userId?: string, companyId?: string) {
  return useQuery({
    queryKey: ['available-systems-for-user', userId, companyId],
    queryFn: async (): Promise<AvailableSystemInfo[]> => {
      if (!userId || !companyId) return [];
      
      // Get systems available in company plans
      const { data: companyPlans, error: plansError } = await supabase
        .from('company_system_plans')
        .select(`
          system_id,
          status,
          systems:system_id (
            id,
            name,
            code,
            app_base_path
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'active');

      if (plansError) throw plansError;

      // Get user's current system assignments
      const { data: userSystems, error: userSystemsError } = await supabase
        .from('user_systems')
        .select('system_id, is_enabled')
        .eq('user_id', userId);

      if (userSystemsError) throw userSystemsError;

      const userSystemMap = userSystems?.reduce((acc, us) => {
        acc[us.system_id] = us.is_enabled;
        return acc;
      }, {} as Record<string, boolean>) || {};

      return companyPlans?.map(plan => {
        const system = Array.isArray(plan.systems) ? plan.systems[0] : plan.systems;
        return {
          id: system?.id || '',
          name: system?.name || '',
          code: system?.code || '',
          app_base_path: system?.app_base_path || '',
          is_available_in_plan: true,
          user_has_access: userSystemMap[plan.system_id] || false
        };
      }) || [];
    },
    enabled: !!userId && !!companyId,
  });
}

export function useUpdateUserSystems(userId: string, companyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (systemIds: string[]) => {
      console.log('Updating user systems:', { userId, systemIds });
      
      // Get current user system assignments
      const { data: currentAssignments, error: fetchError } = await supabase
        .from('user_systems')
        .select('system_id, is_enabled')
        .eq('user_id', userId);

      if (fetchError) {
        console.error('Error fetching current assignments:', fetchError);
        throw fetchError;
      }

      const currentSystemIds = currentAssignments?.map(a => a.system_id) || [];
      const toAdd = systemIds.filter(id => !currentSystemIds.includes(id));
      const toRemove = currentSystemIds.filter(id => !systemIds.includes(id));
      const toUpdate = currentAssignments?.filter(a => 
        systemIds.includes(a.system_id) && !a.is_enabled
      ).map(a => a.system_id) || [];

      console.log('System changes:', { toAdd, toRemove, toUpdate });

      // Remove systems no longer assigned
      if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('user_systems')
          .delete()
          .eq('user_id', userId)
          .in('system_id', toRemove);

        if (deleteError) {
          console.error('Error removing systems:', deleteError);
          throw deleteError;
        }
      }

      // Add new systems
      if (toAdd.length > 0) {
        const { error: insertError } = await supabase
          .from('user_systems')
          .insert(
            toAdd.map(systemId => ({
              user_id: userId,
              system_id: systemId,
              is_enabled: true
            }))
          );

        if (insertError) {
          console.error('Error adding systems:', insertError);
          throw insertError;
        }
      }

      // Enable existing systems that were disabled
      if (toUpdate.length > 0) {
        const { error: updateError } = await supabase
          .from('user_systems')
          .update({ is_enabled: true })
          .eq('user_id', userId)
          .in('system_id', toUpdate);

        if (updateError) {
          console.error('Error updating systems:', updateError);
          throw updateError;
        }
      }

      console.log('User systems updated successfully');
      return { added: toAdd.length, removed: toRemove.length, updated: toUpdate.length };
    },
    onSuccess: (result) => {
      console.log('User systems update success:', result);
      
      // Invalidate related queries
      queryClient.invalidateQueries({ 
        queryKey: ['user-system-assignments', userId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['available-systems-for-user', userId, companyId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['company-users', companyId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['user-systems', userId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['entry-target', userId] 
      });

      toast({
        title: 'Sistemas atualizados',
        description: `${result.added} sistemas adicionados, ${result.removed} removidos, ${result.updated} habilitados`,
      });
    },
    onError: (error) => {
      console.error('Error updating user systems:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao atualizar sistemas do usuário',
        variant: 'destructive',
      });
    },
  });
}